import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { CalendarMonthOutlined, RefreshOutlined } from "@mui/icons-material";

import CustomSnackBar from "../../Components/CustomSnackBar";
import DatePick from "../../Components/DatePick";
import MainText from "../../Components/MainText";
import { useAuth } from "../../context/AuthContext";
import { reportsService } from "../../services";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toMoney = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return `Q${parsed.toFixed(2)}`;
};

const toQueryDate = (value) => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 10);
};

function SalesByDateReportPage() {
  const { currentBranch, user } = useAuth();
  const today = useMemo(() => new Date(), []);
  const branchOptions = useMemo(() => {
    const byId = new Map();
    const userBranches = Array.isArray(user?.user_branches) ? user.user_branches : [];
    userBranches.forEach((row) => {
      const branch = row?.branch;
      if (branch?.id && !byId.has(String(branch.id))) {
        byId.set(String(branch.id), { id: String(branch.id), label: branch.alias || branch.name || branch.legalName || `Sucursal ${branch.id}` });
      }
    });
    if (currentBranch?.id && !byId.has(String(currentBranch.id))) {
      byId.set(String(currentBranch.id), {
        id: String(currentBranch.id),
        label: currentBranch.alias || currentBranch.name || currentBranch.legalName || `Sucursal ${currentBranch.id}`,
      });
    }
    return Array.from(byId.values());
  }, [currentBranch, user?.user_branches]);

  const [filters, setFilters] = useState({
    branch_id: currentBranch?.id ? String(currentBranch.id) : "",
    date_from: today,
    date_to: today,
  });
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    sales_total: "0.00",
    utility_total: "0.00",
    average_sales_per_day: "0.00",
  });
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  useEffect(() => {
    if (!filters.branch_id && currentBranch?.id) {
      setFilters((prev) => ({ ...prev, branch_id: String(currentBranch.id) }));
    }
  }, [currentBranch?.id, filters.branch_id]);

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const loadRows = useCallback(async () => {
    if (!filters.branch_id) return;
    setLoading(true);
    try {
      const data = await reportsService.getSalesByDateReport({
        branch_id: filters.branch_id,
        date_from: toQueryDate(filters.date_from),
        date_to: toQueryDate(filters.date_to),
      });
      setRows(toArray(data?.results || data));
      setTotals(data?.totals || { sales_total: "0.00", utility_total: "0.00", average_sales_per_day: "0.00" });
    } catch {
      setRows([]);
      setTotals({ sales_total: "0.00", utility_total: "0.00", average_sales_per_day: "0.00" });
      showSnack("No se pudo cargar el reporte agrupado por fecha.", "error");
    } finally {
      setLoading(false);
    }
  }, [filters.branch_id, filters.date_from, filters.date_to, showSnack]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const columns = useMemo(
    () => [
      {
        field: "date",
        headerName: "Fecha",
        minWidth: 140,
        flex: 0.7,
      },
      {
        field: "sales_total",
        headerName: "Monto vendido",
        minWidth: 150,
        flex: 0.8,
        valueGetter: (_, row) => toMoney(row?.sales_total),
      },
      {
        field: "utility_total",
        headerName: "Utilidad",
        minWidth: 140,
        flex: 0.7,
        valueGetter: (_, row) => toMoney(row?.utility_total),
      },
      {
        field: "sales_count",
        headerName: "Ventas del dia",
        minWidth: 130,
        flex: 0.7,
      },
      {
        field: "missing_snapshot_sales",
        headerName: "Sin snapshot",
        minWidth: 130,
        flex: 0.7,
      },
    ],
    []
  );

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2}>
        <MainText
          title="Ventas agrupadas por fecha"
          subtitle="Monto y utilidad diaria sobre ventas completadas"
          icon={CalendarMonthOutlined}
        />
        <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={loadRows}>
          Refrescar
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.2 }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={1}>
          <TextField
            select
            size="small"
            label="Sucursal"
            value={filters.branch_id}
            onChange={(event) => setFilters((prev) => ({ ...prev, branch_id: event.target.value }))}
            sx={{ minWidth: 220 }}
          >
            {branchOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <DatePick
            name="date_from"
            label="Desde"
            value={filters.date_from}
            onChange={(name, value) => setFilters((prev) => ({ ...prev, [name]: value }))}
            size="small"
            allowHistoricalDates
          />
          <DatePick
            name="date_to"
            label="Hasta"
            value={filters.date_to}
            onChange={(name, value) => setFilters((prev) => ({ ...prev, [name]: value }))}
            size="small"
            allowHistoricalDates
          />
        </Stack>
      </Paper>

      {rows.some((row) => Number(row?.missing_snapshot_sales || 0) > 0) ? (
        <Alert severity="warning">
          Hay dias con ventas sin snapshot de costo. La utilidad diaria solo suma lineas historicamente consistentes.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 1.2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <Typography variant="body2">Vendido total: {toMoney(totals?.sales_total)}</Typography>
          <Typography variant="body2">Utilidad total: {toMoney(totals?.utility_total)}</Typography>
          <Typography variant="body2">Promedio por dia: {toMoney(totals?.average_sales_per_day)}</Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ height: "calc(100vh - 320px)", minHeight: 420 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          pageSizeOptions={[20, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 20, page: 0 } },
            sorting: { sortModel: [{ field: "date", sort: "asc" }] },
          }}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#757B2B",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover": {
              backgroundColor: "#757B2B !important",
            },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator": {
              color: "#ffffff !important",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(16, 23, 60, 0.08)",
            },
          }}
        />
      </Paper>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}

export default SalesByDateReportPage;
